import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, Send, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../services/api';

const DataCorrectionRequest = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    requestType: 'correction',
    fieldName: '',
    currentValue: '',
    requestedValue: '',
    reason: ''
  });

  const requestTypes = [
    { value: 'correction', label: 'Correcao de Dados', description: 'Corrigir dados pessoais incorretos ou desatualizados' },
    { value: 'deletion', label: 'Eliminacao de Dados', description: 'Solicitar a exclusao de dados pessoais' },
    { value: 'access', label: 'Acesso aos Dados', description: 'Solicitar uma copia dos seus dados pessoais' },
    { value: 'portability', label: 'Portabilidade', description: 'Exportar seus dados para outro sistema' }
  ];

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await api.post('/lgpd/correction-requests', form);
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao enviar solicitacao.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Solicitacao Enviada</h2>
          <p className="text-sm text-gray-600 mb-6">
            Sua solicitacao foi registrada com sucesso. O prazo legal para resposta e de 15 dias uteis.
            Voce pode acompanhar o status na Central de Privacidade.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/dashboard/privacy')}
              className="px-4 py-2 bg-brand-700 text-white rounded-lg text-sm font-medium hover:bg-brand-800"
            >
              Ir para Central de Privacidade
            </button>
            <button
              onClick={() => { setSubmitted(false); setForm({ requestType: 'correction', fieldName: '', currentValue: '', requestedValue: '', reason: '' }); }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Nova Solicitacao
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/privacy')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para Central de Privacidade
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <FileText className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Solicitacao de Dados (LGPD)</h1>
            <p className="text-sm text-gray-600">
              Exerca seus direitos conforme a Lei Geral de Protecao de Dados
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
        {/* Request Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de Solicitacao *
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {requestTypes.map(type => (
              <label
                key={type.value}
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  form.requestType === type.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="requestType"
                  value={type.value}
                  checked={form.requestType === type.value}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 text-brand-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{type.label}</p>
                  <p className="text-xs text-gray-500">{type.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Field Name */}
        {['correction', 'deletion'].includes(form.requestType) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campo / Dado
            </label>
            <input
              type="text"
              name="fieldName"
              value={form.fieldName}
              onChange={handleChange}
              placeholder="Ex: Numero de telefone, Endereco, Nome..."
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
        )}

        {/* Current Value */}
        {form.requestType === 'correction' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor Atual
              </label>
              <input
                type="text"
                name="currentValue"
                value={form.currentValue}
                onChange={handleChange}
                placeholder="Valor que esta incorreto no sistema"
                className="w-full border-gray-300 rounded-lg shadow-sm text-sm focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor Correto
              </label>
              <input
                type="text"
                name="requestedValue"
                value={form.requestedValue}
                onChange={handleChange}
                placeholder="Valor correto que deve ser aplicado"
                className="w-full border-gray-300 rounded-lg shadow-sm text-sm focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </>
        )}

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motivo / Justificativa *
          </label>
          <textarea
            name="reason"
            value={form.reason}
            onChange={handleChange}
            rows={4}
            required
            placeholder="Descreva o motivo da sua solicitacao..."
            className="w-full border-gray-300 rounded-lg shadow-sm text-sm focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        {/* Legal Notice */}
        <div className="p-3 bg-brand-50 border border-brand-200 rounded-lg">
          <p className="text-xs text-brand-800">
            <strong>Prazo legal:</strong> Conforme a LGPD (Art. 18, §5), sua solicitacao sera
            atendida em ate 15 dias uteis. Dados clinicos podem estar sujeitos a retencao
            obrigatoria conforme CFM Resolucao 1821/2007.
          </p>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!form.reason || submitting}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              form.reason && !submitting
                ? 'bg-brand-700 text-white hover:bg-brand-800'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Enviar Solicitacao
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DataCorrectionRequest;
